import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Container,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Alert,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    styled,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { createContact } from '../api/auth';

const SUCCESS_MESSAGE = "We'll get back to you within one working day.";

const SUPPORT_EMAIL = 'medzhub@eshatechgroup.com';
const PHONE_DISPLAY = '+91 8031196612';
const PHONE_TEL = 'tel:+918031196612';

const REACHING_OUT_AS_OPTIONS = [
    { value: 'Therapist', label: 'Therapist' },
    { value: 'Clinic', label: 'Clinic' },
    { value: 'Rehab centre', label: 'Rehab centre' },
    { value: 'Homecare Provider', label: 'Homecare Provider' },
    { value: 'Other', label: 'Other' },
];

const ContactPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: '',
        phone: '',
        reachingOutAs: '',
        message: '',
    });
    const [touched, setTouched] = useState({});
    const [toast, setToast] = useState({ open: false, message: '' });
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const goHomeAfterThanks = () => {
        setSuccessModalOpen(false);
        navigate('/');
    };

    const errors = useMemo(() => {
        const next = {};
        if (!form.name.trim()) next.name = 'Name is required';
        if (!form.phone.trim()) next.phone = 'Phone / WhatsApp number is required';
        if (!form.reachingOutAs) next.reachingOutAs = 'Please select an option';
        if (!form.message.trim()) next.message = 'Message is required';
        return next;
    }, [form]);

    const isValid = Object.keys(errors).length === 0;

    const onChange = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const onBlur = (key) => () => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({ name: true, phone: true, reachingOutAs: true, message: true });

        if (!isValid) {
            setToast({ open: true, message: 'Please fill all required fields.' });
            return;
        }

        setSubmitLoading(true);
        try {
            await createContact({
                name: form.name.trim(),
                phone: form.phone.trim(),
                reachingOutAs: form.reachingOutAs,
                message: form.message.trim(),
            });

            setSuccessModalOpen(true);
            setForm({ name: '', phone: '', reachingOutAs: '', message: '' });
            setTouched({});
        } catch (err) {
            setToast({
                open: true,
                message: err.message || 'Could not submit contact request',
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <Header />
            <Box component="main" sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg">
                    <HeroWrap>
                        <HeroTitle variant="h3" component="h1">
                            Got a question, a partnership idea, or something else entirely?
                        </HeroTitle>
                        <HeroSubtitle>
                            Drop us a message and we'll get back to you.
                        </HeroSubtitle>
                    </HeroWrap>

                    <FormWrap component="section">
                        <FormTitle variant="h5" component="h2">
                            Contact Form
                        </FormTitle>

                        <FormPaper elevation={0}>
                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <FormGrid>
                                    <TextField
                                        label="Name"
                                        value={form.name}
                                        onChange={onChange('name')}
                                        onBlur={onBlur('name')}
                                        error={Boolean(touched.name && errors.name)}
                                        helperText={touched.name && errors.name ? errors.name : ' '}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Phone / WhatsApp number"
                                        value={form.phone}
                                        onChange={onChange('phone')}
                                        onBlur={onBlur('phone')}
                                        error={Boolean(touched.phone && errors.phone)}
                                        helperText={touched.phone && errors.phone ? errors.phone : ' '}
                                        fullWidth
                                    />

                                    <TextField
                                        select
                                        label="I’m reaching out as"
                                        value={form.reachingOutAs}
                                        onChange={onChange('reachingOutAs')}
                                        onBlur={onBlur('reachingOutAs')}
                                        error={Boolean(touched.reachingOutAs && errors.reachingOutAs)}
                                        helperText={touched.reachingOutAs && errors.reachingOutAs ? errors.reachingOutAs : ' '}
                                        fullWidth
                                    >
                                        {REACHING_OUT_AS_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        label="Your message"
                                        value={form.message}
                                        onChange={onChange('message')}
                                        onBlur={onBlur('message')}
                                        error={Boolean(touched.message && errors.message)}
                                        helperText={touched.message && errors.message ? errors.message : ' '}
                                        fullWidth
                                        multiline
                                        minRows={5}
                                    />
                                </FormGrid>

                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={submitLoading}
                                        sx={{
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontWeight: 800,
                                            px: 3,
                                            py: 1.25,
                                            bgcolor: '#0066FF',
                                            '&:hover': { bgcolor: '#0057DB' },
                                        }}
                                    >
                                        {submitLoading ? 'Sending…' : 'Send message'}
                                    </Button>
                                    {/* <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                                        We'll get back to you within one working day.
                                    </Typography> */}
                                </Box>

                                <Collapse in={toast.open} sx={{ mt: 2 }}>
                                    <Alert
                                        severity="error"
                                        onClose={() => setToast({ open: false, message: '' })}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {toast.message}
                                    </Alert>
                                </Collapse>
                            </Box>
                        </FormPaper>
                    </FormWrap>
                    <HighlightPanel elevation={0}>
                        <HighlightTitle variant="h5" component="h2">
                            Prefer to reach out directly?
                        </HighlightTitle>

                        <Typography sx={{ color: '#334155', fontWeight: 700, lineHeight: 1.7 }}>
                            Email us at{' '}
                            <Box
                                component="a"
                                href={`mailto:${SUPPORT_EMAIL}`}
                                sx={{ color: '#0066FF', fontWeight: 900, textDecoration: 'none' }}
                            >
                                {SUPPORT_EMAIL}
                            </Box>{' '}
                            or call{' '}
                            <Box component="a" href={PHONE_TEL} sx={{ color: '#0066FF', fontWeight: 900, textDecoration: 'none' }}>
                                {PHONE_DISPLAY}
                            </Box>
                            {' '}or WhatsApp{' '}
                            <Box component="a" href="https://wa.me/919187644794" target="_blank" rel="noopener noreferrer" sx={{ color: '#0066FF', fontWeight: 900, textDecoration: 'none' }}>
                                +91 9187644794
                            </Box>
                            .
                        </Typography>
                    </HighlightPanel>
                </Container>
            </Box>
            <Footer />

            <Dialog
                open={successModalOpen}
                onClose={goHomeAfterThanks}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: { borderRadius: 3, overflow: 'hidden', position: 'relative' },
                    },
                }}
                aria-labelledby="contact-success-desc"
            >
                <DialogTitle
                    sx={{
                        m: 0,
                        py: 1,
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        minHeight: 48,
                    }}
                >
                    <IconButton
                        onClick={goHomeAfterThanks}
                        aria-label="Close and go to home"
                        sx={{ color: (theme) => theme.palette.grey[600] }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        pt: 0,
                        pb: 2,
                        px: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(46, 125, 50, 0.08)',
                            border: '1px solid rgba(46, 125, 50, 0.22)',
                        }}
                    >
                        <CheckCircleRoundedIcon
                            sx={{
                                fontSize: 40,
                                color: '#2e7d32',
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            id="contact-success-desc"
                            component="p"
                            sx={{ color: '#1b5e20', fontWeight: 600, fontSize: '1rem', lineHeight: 1.5, pt: 0.25, m: 0 }}
                        >
                            {SUCCESS_MESSAGE}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                    <Button
                        variant="contained"
                        onClick={goHomeAfterThanks}
                        autoFocus
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 800,
                            px: 3,
                            bgcolor: '#0066FF',
                            '&:hover': { bgcolor: '#0057DB' },
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const HeroWrap = styled(Box)({
    textAlign: 'left',
    maxWidth: 860,
    marginBottom: 28,
});


const HeroTitle = styled(Typography)({
    fontWeight: 900,
    letterSpacing: '-0.03em',
    color: '#0F172A',
    marginBottom: 10,
    lineHeight: 1.15,
});

const HeroSubtitle = styled(Typography)({
    fontSize: 18,
    color: '#475569',
    fontWeight: 600,
    lineHeight: 1.6,
});

const HighlightPanel = styled(Paper)({
    borderRadius: 18,
    padding: '26px 22px',
    marginTop: 18,
    marginBottom: 34,
    background:
        'linear-gradient(135deg, rgba(0, 102, 255, 0.10) 0%, rgba(99, 102, 241, 0.10) 45%, rgba(14, 116, 144, 0.10) 100%)',
    border: '1px solid rgba(0, 102, 255, 0.18)',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.06)',
});

const HighlightTitle = styled(Typography)({
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#0F172A',
    marginBottom: 14,
});

const FormWrap = styled(Box)({
    marginTop: 6,
});

const FormTitle = styled(Typography)({
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#0F172A',
    marginBottom: 14,
});

const FormPaper = styled(Paper)({
    borderRadius: 18,
    padding: '22px 18px',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
});

const FormGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
    '@media (max-width: 900px)': {
        gridTemplateColumns: '1fr',
    },
});

export default ContactPage;

